import { create } from 'zustand';

import { TMap } from '@shared/types';

import type { TGroundedQaRunResponse } from '../groundedQa/types';
import { type TAiAssistantOrigin, EAiMode, type TAiAssistantSurface, type TAiRunFeedbackInput } from '../types';

type TAssistantState = {
  prompt: string;
  response: TGroundedQaRunResponse | null;
  // Feedback given on the shown answer - lives beside it so a reopened answer still shows it.
  submittedFeedback: TAiRunFeedbackInput | null;
  animatedRunId: string | null;
  // The watched Q&A run - a poll delivery for any other run is dropped.
  pendingRunId: string | null;
  // Kept here, not in local state, so Continue survives the panel closing and reopening.
  followUpPrompt: string | null;
};

type TCloseRequest = { surface: TAiAssistantSurface; kind: 'ask' | 'edit'; proceed: () => void };

type TStore = {
  // The mode the user picked; null until they pick one, so the assistant can offer its own default.
  aiMode: EAiMode | null;
  states: TMap<string, TAssistantState>;
  // Which run kind is generating per surface - written by the mounted assistant, so any code
  // closing it (a screen-mode switch, the overlay toggle, the panel itself) can ask first.
  pendingKind: Partial<Record<TAiAssistantSurface, 'ask' | 'edit'>>;
  // A close waiting for the user's confirm; the assistant renders the dialog and calls proceed.
  closeRequest: TCloseRequest | null;
  setAiMode: (aiMode: EAiMode) => void;
  setPendingKind: (surface: TAiAssistantSurface, kind: 'ask' | 'edit' | null) => void;
  setCloseRequest: (request: TCloseRequest | null) => void;
  read: (surface: TAiAssistantSurface, origin: TAiAssistantOrigin | undefined) => TAssistantState;
  update: (
    surface: TAiAssistantSurface,
    origin: TAiAssistantOrigin | undefined,
    patch: Partial<TAssistantState>
  ) => void;
};

const EMPTY_STATE: TAssistantState = {
  prompt: '',
  response: null,
  submittedFeedback: null,
  animatedRunId: null,
  pendingRunId: null,
  followUpPrompt: null,
};

// Serialization is private to the store; callers always pass the actual origin object.
const buildKey = (surface: TAiAssistantSurface, origin: TAiAssistantOrigin | undefined): string =>
  `${surface}:${origin?.item ?? 'global'}`;

const useAiAssistantStore = create<TStore>((set, get) => ({
  aiMode: null,
  states: {},
  pendingKind: {},
  closeRequest: null,
  setAiMode: (aiMode) => set({ aiMode }),
  setPendingKind: (surface, kind) =>
    set((prev) => ({ pendingKind: { ...prev.pendingKind, [surface]: kind ?? undefined } })),
  setCloseRequest: (request) => set({ closeRequest: request }),
  read: (surface, origin) => get().states[buildKey(surface, origin)] ?? EMPTY_STATE,
  update: (surface, origin, patch) =>
    set((prev) => {
      const key = buildKey(surface, origin);
      return { states: { ...prev.states, [key]: { ...(prev.states[key] ?? EMPTY_STATE), ...patch } } };
    }),
}));

// The one entry point for closing an assistant: closes right away when nothing is generating,
// otherwise parks the close as a request the mounted assistant confirms before proceeding.
export const requestAiAssistantClose = (surface: TAiAssistantSurface, proceed: () => void): void => {
  const { pendingKind, setCloseRequest } = useAiAssistantStore.getState();
  const kind = pendingKind[surface];
  if (kind) setCloseRequest({ surface, kind, proceed });
  else proceed();
};

export default useAiAssistantStore;
